import React, { useState, useEffect, useRef } from "react";
import { X, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface FeedbackPreset {
    linear: string[];
    branches: {
        good: string[];
        average: string[];
        poor: string[];
    };
}

interface Journey {
    title: string;
    description: string;
    cta: string;
    triggerType?: "On Message" | "Run at Specific Time" | "New Lead" | "On Event";
    presetActions?: string[] | FeedbackPreset;
    // "rcs" routes into the RCS Bot Studio (channel-specific bots that
    // hit Twilio + MJ). Absent = existing WhatsApp/journey path.
    channel?: "whatsapp" | "rcs";
}

interface JourneyCategory {
    label: string;
    journeys: Journey[];
}

const journeyData: Record<string, JourneyCategory> = {
    starter: {
        label: "Starter Journeys",
        journeys: [
            {
                title: "Blank Canvas",
                description: "Start with a blank canvas. Design your own bot journey from scratch",
                cta: "Get Started",
            },
            {
                title: "Welcome journeys",
                description:
                    "Greet new contacts automatically. Send a welcome message and introduce users to your business or service.",
                cta: "View Journey",
                triggerType: "On Message",
            },
            {
                title: "Lead Qualification",
                description:
                    "Capture and qualify new leads. Collect user information and route conversations to the right team.",
                cta: "View Journey",
                triggerType: "New Lead",
                presetActions: ["Send Email", "Condition", "WhatsApp Message"],
            },
            {
                title: "Appointment Reminder",
                description:
                    "Reduce missed appointments. Automatically send reminders before scheduled meetings or visits.",
                cta: "View Journey",
                triggerType: "On Event",
                presetActions: ["Wait Until", "Fetch Audience", "Assign Agent"],
            },
            {
                title: "Feedback Collection",
                description:
                    "Collect customer feedback effortlessly. Follow up with users and gather ratings, reviews, or suggestions.",
                cta: "View Journey",
                triggerType: "On Event",
                presetActions: {
                    linear: [
                        "Set Delay Time",
                        "Send Interactive Message",
                    ],
                    branches: {
                        good: [
                            "Store Data",
                            "Create Support Ticket",
                        ],
                        average: [
                            "Store Data",
                            "Send Internal Notification",
                        ],
                        poor: [
                            "Store Data",
                            "Assign Agent",
                        ],
                    },
                }
            },
            {
                title: "Re-engagement Campaign",
                description:
                    "Reconnect with inactive contacts. Send timely follow-ups and revive dormant conversations.",
                cta: "View Journey",
                triggerType: "Run at Specific Time",
            },
            {
                title: "Support Follow-up",
                description:
                    "Keep customers informed. Send updates after support interactions or ticket resolutions.",
                cta: "View Journey",
                triggerType: "On Message",
            },
            {
                title: "Event & Announcement",
                description:
                    "Broadcast important updates. Notify users about events, launches, reminders, or announcements.",
                cta: "View Journey",
                triggerType: "Run at Specific Time",
            },
            // RCS lives inside Starter Journeys per Sree 2026-07-26.
            // Only the Prewired option — Blank was removed 2026-07-27
            // (Joseph: "remove the blank rcs bot"). Operator can still
            // strip the starter down to a bare trigger from the canvas.
            {
                title: "RCS Bot",
                description:
                    "Trigger → Send RCS Template → Wait → chip branches, all pre-connected. Pick a template on the canvas card and branches auto-generate from its chips.",
                cta: "View Journey",
                channel: "rcs",
            },
        ],
    },
    engagement: {
        label: "Customer Engagement",
        journeys: [
            {
                title: "Loyalty Program",
                description: "Reward repeat customers automatically. Send personalized loyalty offers and track points.",
                cta: "View Journey",
                triggerType: "Run at Specific Time",
            },
            {
                title: "Product Onboarding",
                description: "Guide new users through your product features. Increase activation and reduce churn.",
                cta: "View Journey",
                triggerType: "On Message",
            },
            {
                title: "NPS Survey",
                description: "Collect Net Promoter Scores at scale. Understand customer sentiment in real time.",
                cta: "View Journey",
                triggerType: "On Message",
            },
            {
                title: "Birthday Campaign",
                description: "Delight customers on their special day. Send automated birthday messages and offers.",
                cta: "View Journey",
                triggerType: "Run at Specific Time",
            },
        ],
    },
    leads: {
        label: "Lead Management",
        journeys: [
            {
                title: "Inbound Lead Capture",
                description: "Capture inbound leads from any channel and route them to the right sales rep instantly.",
                cta: "View Journey",
                triggerType: "New Lead",
                presetActions: ["Send Email", "Condition", "WhatsApp Message"],
            },
            {
                title: "Lead Nurture Sequence",
                description: "Nurture cold leads with a structured drip sequence. Move them down the funnel automatically.",
                cta: "View Journey",
                triggerType: "Run at Specific Time",
                presetActions: ["Send Email", "Condition", "WhatsApp Message"],
            },
            {
                title: "Demo Booking",
                description: "Let leads book a demo directly in the conversation. Sync with your calendar seamlessly.",
                cta: "View Journey",
                triggerType: "On Message",
            },
            {
                title: "Quote Request",
                description: "Collect quote requests from prospects and notify your sales team for follow-up.",
                cta: "View Journey",
                triggerType: "On Message",
            },
        ],
    },
    reminders: {
        label: "Reminders",
        journeys: [
            {
                title: "Payment Reminder",
                description: "Remind customers about upcoming or overdue payments. Reduce late payments effortlessly.",
                cta: "View Journey",
                triggerType: "Run at Specific Time",
            },
            {
                title: "Subscription Renewal",
                description: "Notify users before their subscription expires. Boost renewal rates with timely nudges.",
                cta: "View Journey",
                triggerType: "Run at Specific Time",
            },
            {
                title: "Service Reminder",
                description: "Prompt customers to schedule routine services. Keep them engaged with your brand.",
                cta: "View Journey",
                triggerType: "Run at Specific Time",
            },
        ],
    },
    notifications: {
        label: "Notifications & Alerts",
        journeys: [
            {
                title: "Order Status Update",
                description: "Keep customers informed at every step of their order. Reduce support queries automatically.",
                cta: "View Journey",
                triggerType: "Run at Specific Time",
            },
            {
                title: "Shipping Alert",
                description: "Send real-time shipping updates. Delight customers with proactive delivery notifications.",
                cta: "View Journey",
                triggerType: "Run at Specific Time",
            },
            {
                title: "System Downtime Alert",
                description: "Notify users of planned or unplanned outages. Maintain trust through transparency.",
                cta: "View Journey",
                triggerType: "Run at Specific Time",
            },
        ],
    },
};

const tabKeys = Object.keys(journeyData) as Array<keyof typeof journeyData>;

interface JourneyModalProps {
    open: boolean;
    onClose: () => void;
}

const JourneyModal: React.FC<JourneyModalProps> = ({ open, onClose }) => {
    const [activeTab, setActiveTab] = useState<string>("starter");
    const [searchQuery, setSearchQuery] = useState("");
    const [visible, setVisible] = useState(false);
    const overlayRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (open) {
            setTimeout(() => setVisible(true), 10);
        } else {
            setVisible(false);
        }
    }, [open]);

    if (!open) return null;

    const currentJourneys = journeyData[activeTab]?.journeys ?? [];

    const filteredJourneys = searchQuery.trim()
        ? Object.values(journeyData)
            .flatMap((cat) => cat.journeys)
            .filter(
                (j) =>
                    j.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    j.description.toLowerCase().includes(searchQuery.toLowerCase())
            )
        : currentJourneys;

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === overlayRef.current) onClose();
    };

    // In JourneyModal.tsx, update the handleJourneyClick function:

    const handleJourneyClick = (journey: Journey) => {
        onClose();

        // RCS is a trigger choice inside the same builder as every other bot
        // now — no separate canvas/route. "Blank" gets just the trigger;
        // anything else gets the prewired template → wait-for-reply example
        // (see the RCS Trigger Node branch in create-builder/index.tsx).
        if (journey.channel === "rcs") {
            const isBlank = journey.title.toLowerCase().includes("blank");
            navigate("/journey/new", {
                state: {
                    journeyPreset: {
                        title: journey.title,
                        triggerType: "RCS Trigger Node",
                        presetActions: isBlank ? [] : ["prewired"],
                    },
                },
            });
            return;
        }

        // For Blank Canvas (WhatsApp-side)
        if (journey.title === "Blank Canvas") {
            navigate("/journey/new");
            return;
        }

        // For all other journeys including those with preset actions
        navigate("/journey/new", {
            state: {
                journeyPreset: {
                    title: journey.title,
                    triggerType: journey.triggerType || "On Message",
                    presetActions: journey.presetActions || [],
                },
            },
        });
    };

    return (
        <div
            ref={overlayRef}
            onClick={handleOverlayClick}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{
                backgroundColor: visible ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0)",
                transition: "background-color 200ms ease",
            }}
        >
            <div
                className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden"
                style={{
                    maxHeight: "75vh",
                    opacity: visible ? 1 : 0,
                    transform: visible ? "scale(1) translateY(0)" : "scale(0.97) translateY(12px)",
                    transition: "opacity 220ms ease, transform 220ms ease",
                }}
            >
                {/* Header */}
                <div className="flex items-start justify-between px-7 pt-7 pb-4 flex-shrink-0">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">
                            Create New Bot Journey
                        </h2>
                        <p className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500 mt-0.5">
                            Create or manage your bot journeys
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Search */}
                        <div className="relative">
                            <Search
                                size={15}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500"
                            />
                            <input
                                type="text"
                                placeholder="Search Journeys"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-xl w-56 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 transition-all placeholder-gray-400 text-gray-700 dark:text-gray-300"
                            />
                        </div>

                        {/* Close */}
                        <button
                            onClick={() => {
                                onClose();
                                navigate("/journey");
                            }}
                            className="w-8 h-8 flex items-center justify-center rounded-xl text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                {!searchQuery.trim() && (
                    <div className="flex items-center gap-0 px-7 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
                        {tabKeys.map((key) => (
                            <button
                                key={key}
                                onClick={() => setActiveTab(key)}
                                className={`relative px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${activeTab === key
                                    ? "text-indigo-600"
                                    : "text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                                    }`}
                            >
                                {journeyData[key].label}
                                {activeTab === key && (
                                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-t-full" />
                                )}
                            </button>
                        ))}
                    </div>
                )}

                {/* Grid */}
                <div className="flex-1 overflow-y-auto px-7 py-5">
                    {filteredJourneys.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-48 text-[#7B7B7B] font-semibold dark:text-gray-500">
                            <Search size={32} className="mb-3 opacity-30" />
                            <p className="text-sm">No journeys found for "{searchQuery}"</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {filteredJourneys.map((journey, i) => (
                                <JourneyCard
                                    key={`${journey.title}-${i}`}
                                    journey={journey}
                                    onJourneyClick={handleJourneyClick}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const JourneyCard: React.FC<{
    journey: Journey;
    onJourneyClick: (journey: Journey) => void;
}> = ({ journey, onJourneyClick }) => {
    const isBlank = journey.title === "Blank Canvas";

    return (
        <div
            className={`flex flex-col justify-between rounded-2xl p-5 border transition-all duration-150 cursor-pointer group ${isBlank
                ? "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-indigo-300 hover:shadow-md"
                : "bg-[#EEF2FF] dark:bg-indigo-500/10 border-transparent hover:border-indigo-200 hover:shadow-md"
                }`}
            style={{ minHeight: "168px" }}
            onClick={() => onJourneyClick(journey)}
        >
            <div>
                {/* Channel label — Sree 2026-07-26: RCS cards live inside
                    Starter Journeys with a small tag so operators can
                    tell them from WhatsApp-side starters. */}
                {journey.channel && (
                    <span
                        className={`inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded mb-2 ${
                            journey.channel === "rcs"
                                ? "bg-indigo-600 text-white"
                                : "bg-emerald-600 text-white"
                        }`}
                    >
                        {journey.channel}
                    </span>
                )}
                <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-2 leading-snug">
                    {journey.title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    {journey.description}
                </p>
            </div>
            <div className="mt-4">
                <span className="text-xs font-semibold text-indigo-600 underline underline-offset-2 group-hover:text-indigo-800 transition-colors">
                    {journey.cta}
                </span>
            </div>
        </div>
    );
};

export default JourneyModal;